import Vue from "vue"
import { EloquentService } from "@/services/api/eloquent.api.service"
import VisitService from "@/services/visit.service"
import {copyObject} from "@/extensions/prototypes/prototypes"
import {roundToTwoDecimals} from "@/services/number.service"
import {fetchVisitTeeth} from "@/services/teeth.helper"

const state = {
    diagnoses: {},
    services: {},
    packages: {},
    materials: {},
    complexServices: {},
    diagnosisTooth: null,
    dropdownServiceIdOpened: null,
    myOpenedVisit: null,
    myOpenedVisits: [],
    laboratoryOrderId: null,
    polises: [],
    visitDictionaries: {},
    serviceCached: {}
}

const getters = {
    materialsAndPackagesByTeeth(state, getters) {
        let materialsArray = []
        let packagesArray = []
        const materialsByTeeth = {}
        const packagesByTeeth = {}
        const materialAmountLeft = {}
        const packageAmountLeft = {}
        const materialsObject = JSON.parse(JSON.stringify(state.materials))
        const packagesObject = JSON.parse(JSON.stringify(state.packages))
        for(const serviceId in materialsObject) {
            materialsObject[serviceId].forEach(material => {
                materialsArray.push({ ...material, serviceId: Number(serviceId), title: material.name })
            })
        }
        for(const serviceId in packagesObject) {
            packagesObject[serviceId].forEach(kit => {
                packagesArray.push({ ...kit, serviceId: Number(serviceId), amount: 1 })
            })
        }
        materialsArray.forEach((item, index) => {
            materialAmountLeft[index] = item.pivot.amount
        })
        packagesArray.forEach((item, index) => {
            packageAmountLeft[index] = item.amount
        })
        getters.recordTeeth.forEach(tooth => {
            materialsByTeeth[tooth] = []
            packagesByTeeth[tooth] = []
            const services = Object.values(state.services).filter(service => service.teeth.includes(tooth))
            services.forEach(service => {
                const amountOfTeethServiceCovers = [...new Set(service.teeth)].length
                // const amountOfTeethServiceCovers = service.teeth.length
                materialsArray.forEach((material, index) => {
                    if(material.serviceId !== Number(service.id)) return
                    const amountToBeAssigned = Math.min(Math.ceil(material.pivot.amount / amountOfTeethServiceCovers), materialAmountLeft[index])
                    if(amountToBeAssigned > 0) {
                        materialAmountLeft[index] -= amountToBeAssigned
                        materialsByTeeth[tooth].push({ ...material, pivot: { ...material.pivot, amount: amountToBeAssigned } })
                    }
                })
                packagesArray.forEach((kit, index) => {
                    if(kit.serviceId !== Number(service.id)) return
                    const amountToBeAssigned = Math.min(Math.ceil(kit.amount / amountOfTeethServiceCovers), packageAmountLeft[index])
                    if(amountToBeAssigned > 0) {
                        packageAmountLeft[index] -= amountToBeAssigned
                        packagesByTeeth[tooth].push(kit)
                    }
                })
            })
        })
        return { materialsByTeeth, packagesByTeeth }
    },
}

const mutations = {
    parseDiagnoses(state, { view, visit }) {
        let arr = {}
        let visit_teeth = view === 'plan' ? 'teeth' : 'record_teeth'
        let tooth_teeth = view === 'plan' ? 'tooth' : 'teeth'
        if(!visit || !visit[visit_teeth]) {
            state.diagnoses = arr
            return
        }
        visit[visit_teeth].forEach(tooth => {
            tooth.diagnoses.forEach(diagnose => {
                if(!arr[diagnose.id]) {
                    diagnose.teeth = [tooth[tooth_teeth]]
                    arr[diagnose.id] = diagnose
                } else {
                    arr[diagnose.id].teeth.push(tooth[tooth_teeth])
                }
            })
        })
        state.diagnoses = arr
    },
    parseComplexServices(state, { view, visit }) {
        let arr = {}
        let visit_teeth = view === 'plan' ? 'teeth' : 'record_teeth'
        let tooth_teeth = view === 'plan' ? 'tooth' : 'teeth'
        if(!visit || !visit[visit_teeth]) {
            state.complexServices = arr
            return
        }
        visit[visit_teeth].forEach(tooth => {
            arr[tooth[tooth_teeth]] = tooth.complex_services
        })
        state.complexServices = arr
    },
    setComplexServices(state, complexServices) {
        state.complexServices = complexServices
    },
    setComplexServiceForTooth(state, { service, tooth }) {
        state.complexServices[tooth] = service
    },
    setDiagnosisTooth(state, tooth) {
        state.diagnosisTooth = tooth
    },
    setDiagnoses(state, value) {
        state.diagnoses = value
    },
    newDiagnosisFound(state, { diagnosis, teeth }) {
        if(!state.diagnoses[diagnosis.id]) {
            diagnosis.teeth = teeth
            Vue.set(state.diagnoses, diagnosis.id, diagnosis)
        } else {
            state.diagnoses[diagnosis.id].teeth = [...new Set([...state.diagnoses[diagnosis.id].teeth, ...teeth])]
        }
    },
    deleteDiagnosis(state, id) {
        Vue.delete(state.diagnoses, id)
    },
    setServices(state, value) {
        state.services = value
    },
    setLaboratoryOrderId(state, value) {
        state.laboratoryOrderId = value
    },
    deleteService(state, id) {
        Vue.delete(state.services, id)
        Vue.delete(state.materials, id)
        Vue.delete(state.packages, id)
        let cnt = 1
        for(var i in state.services) {
            Vue.set(state.services[i], 'order', cnt)
            cnt++
        }
    },
    setPackages(state, value) {
        state.packages = value
    },
    setMaterials(state, value) {
        state.materials = value
    },
    setPolises(state, value) {
        state.polises = value
    },
    setDropdownServiceIdOpened(state, value) {
        state.dropdownServiceIdOpened = value
    },
    nullifyTreatmentCard(state) {
        state.diagnoses = {}
        state.services = {}
        state.packages = {}
        state.materials = {}
        state.complexServices = {}
    },
    setMyOpenedVisit(state, value) {
        state.myOpenedVisit = value
    },
    setMyOpenedVisits(state, value) {
        state.myOpenedVisits = Array.from(
            new Map((value || []).map(item => [Number(item.record_id), item])).values()
        ) // removing duplicate visits
    },
    setVisitDictionaries(state, value) {
        state.visitDictionaries = value
    },
    cleanCachedServices(state) {
        state.serviceCached = {}
    }
}

const actions = {
    newServiceFound({ state, dispatch }, { service, patient, teeth, visit, selectedTooth }) {
        if(!state.services[service.id]) {
            service.teeth = selectedTooth ? [selectedTooth] : teeth
            service.pivot = {
                amount:         service.pivot?.amount ?? 1,
                discount:       service.discount_percent || patient.discount || 0,
                discount_type:  service.discount_type || 'percent',
                price_original: service.cost_branch,
                technic_id:     service.technics?.length > 0 ? service.technics[0].id : null,
                doctor_id:      visit.doctor_id ?? patient.doctor_id,
            }
            service.order = Object.keys(state.services).length + 1
            for(let i = 1; i <= service.teeth.length; i++) {
                if(service.packages && service.packages.length > 0) {
                    if(!(state.packages[service.id])) {
                        Vue.set(state.packages, service.id, [])
                    }
                    state.packages[service.id] = state.packages[service.id].concat(service.packages)
                    service.packages.forEach(p => {
                        dispatch('addMaterialToService', {
                            service_id: service.id,
                            materials: JSON.parse(JSON.stringify(p.materials))
                        })
                    })
                }
                if(!(state.materials[service.id])) {
                    Vue.set(state.materials, service.id, [])
                }
                if(service.materials && service.materials.length > 0) {
                    service.materials.forEach(m => {
                        dispatch('addMaterialToService', {
                            service_id: service.id,
                            materials: [JSON.parse(JSON.stringify(m))]
                        })
                    })
                }
            }
            Vue.set(state.services, service.id, service)
        }
    },
    addMaterialToService({state}, { service_id, materials }) {
        if(!state.materials[service_id]) {
            Vue.set(state.materials, service_id, materials)
            return
        }
        materials.forEach(material => {
            let index = state.materials[service_id].findIndex(el => el.material_id === material.material_id)
            if(index !== -1) {
                state.materials[service_id][index]['pivot']['amount'] = parseFloat(state.materials[service_id][index]['pivot']['amount']) + parseFloat(material['pivot']['amount'])
            } else {
                state.materials[service_id].push(material)
            }
        })
    },
    removePackageMaterialsFromService({ state }, { pack, serviceId }) {
        let removeMaterials = pack.materials
        if(!removeMaterials) return
        let tmpMaterials = JSON.parse(JSON.stringify(state.materials))
        if(serviceId) tmpMaterials = tmpMaterials[serviceId]
        if(!tmpMaterials) return
        removeMaterials.forEach(toremove => {
            tmpMaterials.forEach((current, index) => {
                if(current.material_id == toremove.material_id) {
                    if(parseFloat(toremove.pivot.amount) >= parseFloat(current.pivot.amount)) {
                        tmpMaterials.splice(index, 1)
                    } else {
                        current.pivot.amount -= toremove.pivot.amount
                    }
                }
            })
        })
        if(serviceId) Vue.set(state.materials, serviceId, tmpMaterials)
        if(!serviceId) state.materials = tmpMaterials
    },
    async quantityChangedManually({state, dispatch}, { service_id, sign, teeth, new_tooth, qty }) {
        let teeth_length = teeth.length
        let service = (await EloquentService.show('service', service_id, false, true)).data
        for(var i = 0; i < qty; i++) {
            if(sign === 'minus') {
                if(new_tooth) {
                    const index = teeth.indexOf(new_tooth)
                    if (index !== -1) teeth.splice(index, 1)
                } else {
                    teeth.length = Math.max(0, (teeth_length - 1))
                }
                if(service.packages && service.packages.length > 0) {
                    let statePackages = state.packages[service.id]
                    service.packages.forEach(p => {
                        const index = statePackages.findIndex(x => x.package_id === p.package_id)
                        if(index > -1) statePackages.splice(index, 1)
                        Vue.set(state.packages, service.id, statePackages)
                        dispatch('removePackageMaterialsFromService', {
                            serviceId: service.id,
                            pack: p
                        })
                    })
                }
                if(service.materials && service.materials.length > 0) {
                    dispatch('removePackageMaterialsFromService', { serviceId: service.id, pack: { materials: service.materials }})
                }
            }
            if(sign === 'plus') {
                // let new_tooth = this.teeth.find(t => !teeth.includes(t))
                // let new_tooth = null // decided not to add new tooth on plus, cause otherwise it is impossible to add more than 1 amount on 1 tooth without selecting other tooth
                if(!new_tooth && teeth_length > 0) new_tooth = teeth[(teeth_length - 1)]
                if(!new_tooth) new_tooth = teeth[0]
                teeth.push(new_tooth)

                if(service.packages && service.packages.length > 0) {
                    state.packages[service.id] = state.packages[service.id].concat(service.packages)
                    service.packages.forEach(p => {
                        dispatch('addMaterialToService', { service_id: service.id, materials: p.materials})
                    })
                }
                if(service.materials && service.materials.length > 0) {
                    service.materials.forEach(m => {
                        dispatch('addMaterialToService', { service_id: service.id, materials: [m]})
                    })
                }
            }
        }
        if(!state.services[service_id]) state.services[service_id] = {}
        Vue.set(state.services[service_id], 'teeth', teeth)
    },
    nullifyOpenedVisit(context) {
        context.commit('setMyOpenedVisit', null)
        context.commit('setMyOpenedVisits', [])
    },
    async toggleServiceToToothNewVersion({ state, rootState, dispatch }, { serviceId, recordTooth, teethNames, qty, sign, scope, insuranceCompanyId }) {
        // console.log(serviceId, recordTooth, qty, sign, scope);
        const visit = copyObject(rootState.patient.visit) // we do in case there are lot's of qty, so not to make vue recalculate everything at each
        const cacheIndex = `${serviceId}_${insuranceCompanyId || ''}`
        let service = state.serviceCached[cacheIndex]
        if(!service) {
            service = (await EloquentService.show('service', serviceId, false, true, { insuranceCompanyId })).data
            state.serviceCached[cacheIndex] = copyObject(service)
        }
        service = copyObject(service)
        const existingToothService = (recordTooth?.services || []).find(serv => Number(serv.id) === Number(serviceId))
        if(!qty) qty = 1
        if(!scope) scope = 'group'
        const patient = rootState.patient.patient
        const teeth = fetchVisitTeeth(visit)
        for(let i = 0; i < qty; i++) {
            for(const visitTooth of teeth) {
                let toothIsInScope = false
                if(scope === 'group' && visitTooth.group_index === recordTooth.group_index) toothIsInScope = true
                if(scope === 'tooth' && visitTooth.teeth === recordTooth.teeth) toothIsInScope = true
                if(scope === 'all_teeth') toothIsInScope = true
                // batches many teeth into a single dispatch (and a single visit copy) instead of one dispatch per tooth, since applying a service to N teeth one-by-one froze the UI for large tooth selections
                if(scope === 'teeth_list' && (teethNames || []).includes(visitTooth.teeth)) toothIsInScope = true
                if(toothIsInScope) {
                    // first we add / remove service itself (cause for materials and packages we might need to search for tooth with this service)
                    if(sign === 'plus') {
                        const pivot = (scope === 'teeth_list'
                            ? visitTooth.services.find(serv => Number(serv.id) === Number(serviceId))
                            : existingToothService)?.pivot
                        visitTooth.services.push({
                            ...service,
                            pivot: {
                                discount:           pivot?.discount || service.discount_percent || patient.discount || 0,
                                discount_absolute:  pivot?.discount_absolute || service.discount_absolute || 0,
                                discount_type:      pivot?.discount_type || service.discount_type || 'percent',
                                price_original:     pivot?.price_original || service.cost_branch,
                                technic_id:         service.technics?.length > 0 ? service.technics[0].id : null,
                                doctor_id:          visit.doctor_id ?? patient.doctor_id,
                            },
                        })
                    }
                    if(sign === 'minus') {
                        VisitService.removeOneItemFromArray(service, visitTooth.services, 'id')
                    }
                    if((service.packages || []).length > 0) {
                        for(const pack of service.packages) {
                            if(sign === 'plus') {
                                await dispatch('addPackageToSummaryService', { serviceId, pack, visitTooth })
                            }
                            if(sign === 'minus') {
                                await dispatch('removePackageFromSummaryService', { serviceId, pack, visitTooth })
                            }
                        }
                    }
                    if((service.materials || []).length > 0) {
                        for(const material of service.materials) {
                            if(sign === 'plus') {
                                await dispatch('addMaterialToRandomToothWithService', {
                                    serviceId,
                                    material,
                                    visitTooth,
                                    qty: material.pivot?.amount
                                })
                            }
                            if(sign === 'minus') {
                                await dispatch('removeMaterialFromSummaryService', { serviceId, material, visitTooth, qty: material.pivot?.amount })
                            }
                        }
                    }
                }
            }
        }
        rootState.patient.visit = visit
    },
    async removeServiceFromToothGroup({ rootState }, { teeth, serviceId }) {
        const visit = copyObject(rootState.patient.visit)
        const visitTeeth = fetchVisitTeeth(visit)
        for(const visitTooth of visitTeeth) {
            if(teeth === null || teeth.includes(visitTooth.teeth)) {
                visitTooth.services = visitTooth.services.filter(s => Number(s.id) !== Number(serviceId))
                visitTooth.materials = visitTooth.materials.filter(m => !m.pivot?.service_id || Number(m.pivot?.service_id) !== Number(serviceId))
                visitTooth.packages = visitTooth.packages.filter(p => !p.pivot?.service_id || Number(p.pivot?.service_id) !== Number(serviceId))
            }
        }
        rootState.patient.visit = visit
    },
    async findMaterialFromServices({ dispatch }, { serviceId, materialId, visitTooth }) {
        if(!visitTooth) visitTooth = await dispatch('findToothWithServiceId', { serviceId })
        return (visitTooth?.materials || []).find(material => material.material_id === materialId && (!serviceId || Number(material.pivot?.service_id) === serviceId))
    },
    findToothWithServiceId({ rootGetters }, { serviceId }) {
        const visitTeeth = rootGetters.recordTeethObjects
        return visitTeeth.find(t => (t.services || []).find(s => s.id === serviceId))
    },
    findToothWithServiceIdAndMaterial({ rootGetters }, { serviceId, materialId }) {
        const visitTeeth = rootGetters.recordTeethObjects
        return visitTeeth.find(t => (t.services || []).find(s => s.id === serviceId) && (t.materials || []).find(m => m.material_id === materialId))
    },
    findToothWithServiceIdAndPackage({ rootGetters }, { serviceId, packageId }) {
        const visitTeeth = rootGetters.recordTeethObjects
        return visitTeeth.find(t => (t.services || []).find(s => s.id === serviceId) && (t.packages || []).find(m => m.package_id === packageId))
    },
    async addMaterialToRandomToothWithService({ dispatch }, { serviceId, material, visitTooth, qty }) {
        if(!qty) qty = material?.pivot?.amount || 1
        const materialFound = await dispatch('findMaterialFromServices', {
            serviceId,
            materialId: material.material_id || material.id,
            visitTooth
        })
        if(materialFound) {
            materialFound.pivot.amount = roundToTwoDecimals(Number(materialFound.pivot.amount) + qty)
            return
        }
        if(!visitTooth) visitTooth = await dispatch('findToothWithServiceId', { serviceId })
        if(visitTooth) {
            if(!material.pivot) material.pivot = {}
            if(!material.pivot.amount) material.pivot.amount = qty
            if(!material.pivot.service_id) material.pivot.service_id = serviceId
            if(!material.name && material.title) material.name = material.title
            if(!material.material_id && material.id) material.material_id = material.id
            visitTooth.materials.push(material)
        }
    },
    removeMaterialFromEachService({ rootGetters }, { serviceId, material }) {
        const visitTeeth = rootGetters.recordTeethObjects
        for(const visitTooth of visitTeeth) {
            for(const toothMaterialIndex in (visitTooth.materials || [])) {
                const toothMaterial = visitTooth.materials[toothMaterialIndex]
                if(toothMaterial.material_id === material.material_id && toothMaterial.pivot.service_id === serviceId) {
                    visitTooth.materials.splice(toothMaterialIndex, 1)
                }
            }
        }
    },
    async addPackageToSummaryService({ dispatch }, { serviceId, pack, visitTooth }) {
        if(!visitTooth) visitTooth = await dispatch('findToothWithServiceId', { serviceId })
        if(visitTooth) {
            for(const material of (pack.materials || [])) {
                await dispatch('addMaterialToRandomToothWithService', { serviceId, material, visitTooth })
            }
            if(!pack.pivot) pack.pivot = {}
            if(!pack.pivot.service_id) pack.pivot.service_id = serviceId
            visitTooth.packages.push(pack)
        }
    },
    async removePackageFromSummaryService({ dispatch }, { pack, serviceId, visitTooth }) {
        for(const material of (pack.materials || [])) {
            await dispatch('removeMaterialFromSummaryService', { material, serviceId, visitTooth })
        }
        let toothWithPackage = null
        if(visitTooth) toothWithPackage = visitTooth
        if(!visitTooth) toothWithPackage = await dispatch('findToothWithServiceIdAndPackage', { serviceId, packageId: pack.package_id })
        VisitService.removeOneItemFromArray(pack, toothWithPackage?.packages, 'package_id')
    },
    async removeMaterialFromSummaryService({ dispatch }, { material, serviceId, visitTooth, qty }) {
        if(!qty) qty = material.pivot.amount || 1
        let tooth = null
        if(visitTooth) tooth = visitTooth
        if(!visitTooth) tooth = await dispatch('findToothWithServiceIdAndMaterial', { serviceId, materialId: material.material_id })
        const materialIndex = (tooth?.materials || []).findIndex(m => m.material_id === material.material_id)
        if(materialIndex !== -1 && tooth) {
            tooth.materials[materialIndex].pivot.amount = roundToTwoDecimals(tooth.materials[materialIndex].pivot.amount - qty)
            if(tooth.materials[materialIndex].pivot.amount <= 0) {
                tooth.materials.splice(materialIndex, 1)
            }
        }
    },
    async copyEverythingFromToothToTooth({ dispatch }, { sourceTooth, destinationTooth, canChangeServices, insuranceCompanyId }) {
        const textsToCopy = ['description', 'medication', 'plancure', 'plansurvey', 'recommendations']
        textsToCopy.forEach(text => {
            destinationTooth[text] = sourceTooth[text]
        })
        destinationTooth.diagnoses = copyObject(sourceTooth.diagnoses)
        destinationTooth.preliminary_diagnoses = copyObject(sourceTooth.preliminary_diagnoses)
        if(canChangeServices) {
            destinationTooth.complex_services = copyObject(sourceTooth.complex_services)
            for(const service of destinationTooth.services) { // removing old services
                await dispatch('removeServiceFromToothGroup', {
                    teeth: [destinationTooth.teeth || destinationTooth.tooth], serviceId: service.id
                })
            }
            for(const service of sourceTooth.services) {
                await dispatch('toggleServiceToToothNewVersion', {
                    serviceId: service.id,
                    recordTooth: destinationTooth,
                    qty: 1,
                    sign: 'plus',
                    scope: 'tooth',
                    insuranceCompanyId
                })
            }
        }
    }
};

export default {
    state,
    actions,
    mutations,
    getters
}
